import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { publicAPI } from '../../services/api';
import toast from 'react-hot-toast';

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    if (window.innerWidth < 768) {
      setOpenSection(openSection === section ? null : section);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;

    const loadingToast = toast.loading('Subscribing to updates...');
    try {
      const response = await publicAPI.subscribeNewsletter(email);
      toast.dismiss(loadingToast);

      if (response.data.success) {
        if (response.data.alreadySubscribed) {
          toast.success('You are already in our live notification pipeline!', { icon: '🔔' });
        } else {
          toast.success("Successfully subscribed to DDU learning updates!", { icon: '🎉' });
        }
        e.target.reset();
      } else {
        toast.error(response.data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      console.error('Newsletter subscription error:', err);
      toast.error(err.response?.data?.message || 'Subscription failed. Please check your network.');
    }
  };

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 text-white mt-20 border-t border-white/10 overflow-hidden">
      {/* Decorative background glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column - Always expanded */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-all duration-300 group">
              <div className="p-2.5 bg-blue-600/10 rounded-xl border border-blue-500/30 group-hover:bg-blue-600/20 group-hover:scale-110 transition-all duration-300">
                <Icons.GraduationCap className="w-8 h-8 text-blue-400 group-hover:text-blue-300" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight uppercase bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Dire Dawa University</h3>
                <p className="text-blue-400/80 text-[10px] font-bold uppercase tracking-widest mt-0.5">Learning Materials</p>
              </div>
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed max-w-sm">
              Empowering students and educators with seamless, centralized access to verified academic resources and tools.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Icons.Facebook, href: "https://facebook.com", label: "Facebook", color: "hover:bg-blue-600 hover:text-white" },
                { icon: Icons.Twitter, href: "https://twitter.com", label: "Twitter", color: "hover:bg-sky-500 hover:text-white" },
                { icon: Icons.Linkedin, href: "https://linkedin.com", label: "LinkedIn", color: "hover:bg-blue-700 hover:text-white" },
                { icon: Icons.Instagram, href: "https://instagram.com", label: "Instagram", color: "hover:bg-pink-600 hover:text-white" }
              ].map((social, idx) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={idx}
                    href={social.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`p-2.5 bg-white/5 border border-white/10 rounded-xl text-slate-300 transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${social.color}`}
                  >
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-4 md:space-y-6 border-b border-white/5 pb-4 md:border-b-0 md:pb-0">
            <button 
              onClick={() => toggleSection('navigation')}
              className="w-full text-left flex items-center justify-between md:pointer-events-none focus:outline-none"
            >
              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-400">Quick Navigation</h4>
              <Icons.ChevronDown className={`w-4 h-4 text-slate-400 md:hidden transition-transform duration-300 ${openSection === 'navigation' ? 'rotate-180 text-blue-400' : ''}`} />
            </button>
            <div className={`transition-all duration-300 overflow-hidden md:max-h-none md:opacity-100 ${openSection === 'navigation' ? 'max-h-60 opacity-100 mt-4' : 'max-h-0 opacity-0 md:mt-0'}`}>
              <ul className="grid gap-3.5">
                {[
                  { to: "/", label: "Home", icon: Icons.Home },
                  { to: "/browse", label: "Browse Schools", icon: Icons.School },
                  { to: "/search", label: "Search Materials", icon: Icons.Search },
                  { to: "/dashboard", label: "My Dashboard", icon: Icons.LayoutDashboard },
                  { to: "/feedback", label: "Send Feedback", icon: Icons.MessageSquare }
                ].map((link, idx) => {
                  const IconComponent = link.icon;
                  return (
                    <li key={idx}>
                      <Link 
                        to={link.to} 
                        className="text-slate-300 hover:text-blue-400 transition-all duration-300 flex items-center gap-2.5 text-sm group"
                      >
                        <IconComponent className="w-4 h-4 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all duration-300" />
                        <span className="group-hover:translate-x-1 transition-transform duration-300">{link.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Contact Info Column */}
          <div className="space-y-4 md:space-y-6 border-b border-white/5 pb-4 md:border-b-0 md:pb-0">
            <button 
              onClick={() => toggleSection('contact')}
              className="w-full text-left flex items-center justify-between md:pointer-events-none focus:outline-none"
            >
              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-400">Get in Touch</h4>
              <Icons.ChevronDown className={`w-4 h-4 text-slate-400 md:hidden transition-transform duration-300 ${openSection === 'contact' ? 'rotate-180 text-blue-400' : ''}`} />
            </button>
            <div className={`transition-all duration-300 overflow-hidden md:max-h-none md:opacity-100 ${openSection === 'contact' ? 'max-h-72 opacity-100 mt-4' : 'max-h-0 opacity-0 md:mt-0'}`}>
              <ul className="grid gap-4">
                <li className="flex items-start gap-3 group">
                  <div className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-400 group-hover:bg-blue-500/10 transition-colors">
                    <Icons.MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Campus Location</span>
                    <span className="text-slate-200 text-sm font-medium">Dire Dawa, Ethiopia</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 group">
                  <div className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-400 group-hover:bg-blue-500/10 transition-colors">
                    <Icons.Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Registrar Support</span>
                    <span className="text-slate-200 text-sm font-medium">+251 25 111 0000</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 group">
                  <div className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-400 group-hover:bg-blue-500/10 transition-colors">
                    <Icons.Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Email Inquiries</span>
                    <a href="mailto:learning@ddu.edu.et" className="text-slate-200 hover:text-blue-400 text-sm font-medium transition-colors">
                      learning@ddu.edu.et
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter Column */}
          <div className="space-y-4 md:space-y-6 pb-4 md:pb-0">
            <button 
              onClick={() => toggleSection('subscribe')}
              className="w-full text-left flex items-center justify-between md:pointer-events-none focus:outline-none"
            >
              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-400">Stay Synchronized</h4>
              <Icons.ChevronDown className={`w-4 h-4 text-slate-400 md:hidden transition-transform duration-300 ${openSection === 'subscribe' ? 'rotate-180 text-blue-400' : ''}`} />
            </button>
            <div className={`transition-all duration-300 overflow-hidden md:max-h-none md:opacity-100 ${openSection === 'subscribe' ? 'max-h-64 opacity-100 mt-4' : 'max-h-0 opacity-0 md:mt-0'}`}>
              <div className="space-y-4">
                <p className="text-slate-300 text-sm leading-relaxed">
                  Subscribe to our notification pipeline for live updates on curriculum uploads.
                </p>
                <form 
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-2"
                >
                  <input 
                    name="email"
                    type="email" 
                    placeholder="Enter email address" 
                    className="flex-1 px-4 py-3 rounded-xl bg-slate-900/60 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all shadow-inner"
                    required
                  />
                  <button 
                    type="submit"
                    className="px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 active:scale-95 text-white rounded-xl font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-500/20"
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Separator line */}
        <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mt-16 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left text-slate-400 text-xs">
          <p>&copy; {new Date().getFullYear()} Dire Dawa University. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Academic Platform</span>
            <span className="text-slate-600">•</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Designed for DDU</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;